import React, { useState, useRef, useCallback } from 'react'
import {
  X,
  Upload,
  Camera,
  Loader,
  AlertCircle,
  CheckCircle,
  Syringe,
  Calendar,
  Building2,
  Factory,
  Hash,
  Plus,
  Trash2,
  AlertTriangle,
  Image as ImageIcon,
  RefreshCw,
  Globe
} from 'lucide-react'
import { mdApi } from '../api/mdApi'
import { VISION_CONTEXTS, validateExtractedData } from '../config/vision_contexts'
import './VaccinationUploadDialog.css'

/**
 * VaccinationUploadDialog - Dialog for uploading and processing vaccination card images
 *
 * Workflow:
 * 1. Image Capture/Upload - User uploads or captures vaccination card image
 * 2. AI Analysis - Image sent to Vision AI for data extraction
 * 3. Review & Edit - User reviews and edits extracted vaccination data
 * 4. Save - Validated data saved to patient record
 *
 * @param {boolean} isOpen - Whether the dialog is visible
 * @param {Function} onClose - Callback when dialog is closed
 * @param {string} patientId - Patient ID to add vaccinations to
 * @param {string} patientName - Patient name for display/validation
 * @param {string} patientDob - Patient DOB for validation
 * @param {Array} existingVaccinations - Existing vaccinations to check for duplicates
 * @param {Function} onSuccess - Callback when vaccinations are successfully saved
 */
function VaccinationUploadDialog({
  isOpen,
  onClose,
  patientId,
  patientName,
  patientDob,
  existingVaccinations = [],
  onSuccess
}) {
  // Workflow state
  const [step, setStep] = useState('upload') // 'upload' | 'analyzing' | 'review' | 'saving'
  
  // Image state
  const [selectedFile, setSelectedFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  
  // Analysis state
  const [analysisResult, setAnalysisResult] = useState(null)
  const [analysisError, setAnalysisError] = useState(null)
  
  // Form state - array of vaccination records being edited
  const [vaccinations, setVaccinations] = useState([])
  
  // Patient info warning
  const [patientMismatch, setPatientMismatch] = useState(null)
  
  // Duplicate warnings
  const [duplicateWarnings, setDuplicateWarnings] = useState({})
  
  // Save state
  const [saveError, setSaveError] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  
  // Refs
  const fileInputRef = useRef(null)

  // Reset dialog state
  const resetDialog = useCallback(() => {
    setStep('upload')
    setSelectedFile(null)
    setImagePreview(null)
    setAnalysisResult(null)
    setAnalysisError(null)
    setVaccinations([])
    setPatientMismatch(null)
    setDuplicateWarnings({})
    setSaveError(null)
    setSaveSuccess(false)
  }, [])

  // Handle dialog close
  const handleClose = () => {
    resetDialog()
    onClose()
  }

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      setAnalysisError('Please upload a JPEG, PNG, WebP image, or PDF file.')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setAnalysisError('File size must be less than 10MB.')
      return
    }

    setSelectedFile(file)
    setAnalysisError(null)

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (e) => setImagePreview(e.target.result)
      reader.readAsDataURL(file)
    } else {
      setImagePreview(null) // PDF doesn't have preview
    }
  }

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer.files?.[0]
    if (file) {
      const event = { target: { files: [file] } }
      handleFileSelect(event)
    }
  }

  // Analyze the uploaded image
  const handleAnalyze = async () => {
    if (!selectedFile) return

    setStep('analyzing')
    setAnalysisError(null)

    try {
      const result = await mdApi.analyzeImage(selectedFile, 'vaccination_card', patientId)
      
      setAnalysisResult(result)

      // Extract data from nested structure (API returns {success, data: {...}})
      const analysisData = result.data || result

      // Check if valid vaccination card
      if (!analysisData.is_valid_content) {
        setAnalysisError(
          `This doesn't appear to be a vaccination card. Detected: ${analysisData.detected_content || 'unknown content'}`
        )
        setStep('upload')
        return
      }

      // Extract vaccinations from result
      const extractedVaccinations = analysisData.vaccinations || []
      
      if (extractedVaccinations.length === 0) {
        setAnalysisError('No vaccination records could be extracted from this image. Please try a clearer image.')
        setStep('upload')
        return
      }

      // Transform to form format and add metadata
      const formVaccinations = extractedVaccinations.map((vax, idx) => ({
        id: `new-${idx}-${Date.now()}`,
        vaccine_type: vax.vaccine_type || '',
        date_administered: vax.date_administered || '',
        dose_label: vax.dose_label || '',
        manufacturer: vax.manufacturer || '',
        lot_number: vax.lot_number || '',
        healthcare_site: vax.healthcare_site || '',
        source: 'vision_upload',
        isNew: true
      }))

      setVaccinations(formVaccinations)

      // Check for patient info mismatch
      const patientInfo = analysisData.patient_info
      if (patientInfo) {
        const extractedName = patientInfo.full_name?.toLowerCase() || ''
        const extractedDob = patientInfo.date_of_birth || ''
        const currentName = patientName?.toLowerCase() || ''
        
        if (extractedName && !currentName.includes(extractedName.split(' ')[0])) {
          setPatientMismatch({
            extractedName: patientInfo.full_name,
            extractedDob: patientInfo.date_of_birth,
            currentName: patientName,
            currentDob: patientDob
          })
        }
      }

      // Check for duplicates against existing vaccinations
      checkForDuplicates(formVaccinations)

      setStep('review')
    } catch (error) {
      console.error('Vision analysis error:', error)
      setAnalysisError(error.message || 'Failed to analyze image. Please try again.')
      setStep('upload')
    }
  }

  // Check for duplicate vaccinations
  const checkForDuplicates = (vaxList) => {
    const warnings = {}
    vaxList.forEach((vax) => {
      const isDuplicate = existingVaccinations.some(
        (existing) =>
          existing.vaccine_type === vax.vaccine_type &&
          existing.date_administered === vax.date_administered
      )
      if (isDuplicate) {
        warnings[vax.id] = `A ${vax.vaccine_type} vaccination on ${vax.date_administered} already exists`
      }
    })
    setDuplicateWarnings(warnings)
  }

  // Update a vaccination field
  const updateVaccination = (id, field, value) => {
    setVaccinations((prev) =>
      prev.map((vax) => (vax.id === id ? { ...vax, [field]: value } : vax))
    )
    
    // Re-check duplicates if vaccine_type or date changes
    if (field === 'vaccine_type' || field === 'date_administered') {
      setTimeout(() => {
        checkForDuplicates(vaccinations.map((vax) => 
          vax.id === id ? { ...vax, [field]: value } : vax
        ))
      }, 0)
    }
  }

  // Remove a vaccination from the list
  const removeVaccination = (id) => {
    setVaccinations((prev) => prev.filter((vax) => vax.id !== id))
    setDuplicateWarnings((prev) => {
      const newWarnings = { ...prev }
      delete newWarnings[id]
      return newWarnings
    })
  }

  // Add a manual vaccination entry
  const addEmptyVaccination = () => {
    const newVax = {
      id: `manual-${Date.now()}`,
      vaccine_type: '',
      date_administered: '',
      dose_label: '',
      manufacturer: '',
      lot_number: '',
      healthcare_site: '',
      source: 'manual_entry',
      isNew: true
    }
    setVaccinations((prev) => [...prev, newVax])
  }

  // Validate a single vaccination
  const validateVaccination = (vax) => {
    const errors = []
    
    if (!vax.vaccine_type?.trim()) {
      errors.push('Vaccine type is required')
    }
    
    if (!vax.date_administered) {
      errors.push('Date administered is required')
    } else {
      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (!dateRegex.test(vax.date_administered)) {
        errors.push('Date must be in YYYY-MM-DD format')
      } else {
        const adminDate = new Date(vax.date_administered)
        const today = new Date()
        
        // Not in future
        if (adminDate > today) {
          errors.push('Date cannot be in the future')
        }
        
        // After patient DOB
        if (patientDob) {
          const dobDate = new Date(patientDob)
          if (adminDate < dobDate) {
            errors.push('Date cannot be before patient\'s birth date')
          }
        }
      }
    }
    
    return errors
  }

  // Validate all vaccinations
  const validateAllVaccinations = () => {
    const allErrors = {}
    let isValid = true
    
    vaccinations.forEach((vax) => {
      const errors = validateVaccination(vax)
      if (errors.length > 0) {
        allErrors[vax.id] = errors
        isValid = false
      }
    })
    
    return { isValid, errors: allErrors }
  }

  // Save vaccinations
  const handleSave = async () => {
    setSaveError(null)
    
    // Validate
    const { isValid, errors } = validateAllVaccinations()
    if (!isValid) {
      const errorMessages = Object.values(errors).flat()
      setSaveError(`Please fix the following errors: ${errorMessages.join(', ')}`)
      return
    }
    
    if (vaccinations.length === 0) {
      setSaveError('No vaccinations to save')
      return
    }

    setStep('saving')

    try {
      // Prepare data for API
      const vaccinationsToSave = vaccinations.map((vax) => ({
        vaccine_type: vax.vaccine_type.trim(),
        date_administered: vax.date_administered,
        dose_label: vax.dose_label?.trim() || null,
        manufacturer: vax.manufacturer?.trim() || null,
        lot_number: vax.lot_number?.trim() || null,
        healthcare_site: vax.healthcare_site?.trim() || null,
        source: vax.source,
        source_language: analysisResult?.data?.detected_language || 'English',
        uploaded_at: new Date().toISOString()
      }))

      await mdApi.addPatientVaccinations(patientId, vaccinationsToSave)
      
      setSaveSuccess(true)
      
      // Callback and close after short delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess()
        }
      }, 1500)
    } catch (error) {
      console.error('Save error:', error)
      setSaveError(error.message || 'Failed to save vaccinations. Please try again.')
      setStep('review')
    }
  }

  // Retry with new image
  const handleRetry = () => {
    setSelectedFile(null)
    setImagePreview(null)
    setAnalysisError(null)
    setStep('upload')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (!isOpen) return null

  return (
    <div className="vaccination-dialog-overlay" onClick={handleClose}>
      <div 
        className="vaccination-dialog" 
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vaccination-dialog-title"
      >
        {/* Header */}
        <div className="vaccination-dialog-header">
          <div className="dialog-title-row">
            <Syringe size={24} className="dialog-icon" />
            <h2 id="vaccination-dialog-title">Add Vaccination Record</h2>
          </div>
          <button 
            className="dialog-close-btn" 
            onClick={handleClose}
            aria-label="Close dialog"
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="dialog-steps">
          <div className={`step ${step === 'upload' ? 'active' : ''} ${['analyzing', 'review', 'saving'].includes(step) ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Upload</span>
          </div>
          <div className="step-connector" />
          <div className={`step ${step === 'analyzing' ? 'active' : ''} ${['review', 'saving'].includes(step) ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Analyze</span>
          </div>
          <div className="step-connector" />
          <div className={`step ${step === 'review' ? 'active' : ''} ${step === 'saving' ? 'completed' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Review</span>
          </div>
          <div className="step-connector" />
          <div className={`step ${step === 'saving' ? 'active' : ''} ${saveSuccess ? 'completed' : ''}`}>
            <span className="step-number">4</span>
            <span className="step-label">Save</span>
          </div>
        </div>

        {/* Content */}
        <div className="vaccination-dialog-content">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="upload-step">
              <div 
                className={`upload-area ${selectedFile ? 'has-file' : ''}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {imagePreview ? (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Vaccination card preview" />
                    <button 
                      className="change-image-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <RefreshCw size={16} />
                      Change Image
                    </button>
                  </div>
                ) : selectedFile ? (
                  <div className="file-selected">
                    <ImageIcon size={48} />
                    <p className="file-name">{selectedFile.name}</p>
                    <button 
                      className="change-image-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <RefreshCw size={16} />
                      Change File
                    </button>
                  </div>
                ) : (
                  <div className="upload-prompt">
                    <Upload size={48} className="upload-icon" />
                    <p className="upload-text">
                      Drag and drop a vaccination card image here
                    </p>
                    <p className="upload-subtext">or</p>
                    <button 
                      className="btn-browse"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Camera size={16} />
                      Browse Files
                    </button>
                    <p className="upload-hint">
                      Supports JPEG, PNG, WebP, and PDF (max 10MB)
                    </p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              {analysisError && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  {analysisError}
                </div>
              )}

              <div className="upload-actions">
                <button className="btn-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleAnalyze}
                  disabled={!selectedFile}
                >
                  <Syringe size={16} />
                  Analyze Card
                </button>
              </div>
            </div>
          )}

          {/* Analyzing Step */}
          {step === 'analyzing' && (
            <div className="analyzing-step">
              <div className="analyzing-spinner">
                <Loader size={48} className="spinner" />
              </div>
              <h3>Analyzing Vaccination Card...</h3>
              <p>Next step: review the information extracted and adjust any value.</p>
            </div>
          )}

          {/* Review Step */}
          {step === 'review' && (
            <div className="review-step">
              {/* Patient Mismatch Warning */}
              {patientMismatch && (
                <div className="warning-banner patient-mismatch">
                  <AlertTriangle size={20} />
                  <div className="warning-content">
                    <strong>Patient Information Mismatch</strong>
                    <p>
                      The vaccination card shows <strong>{patientMismatch.extractedName}</strong>
                      {patientMismatch.extractedDob && ` (DOB: ${patientMismatch.extractedDob})`},
                      but you're adding records to <strong>{patientMismatch.currentName}</strong>.
                      Please verify this is the correct patient.
                    </p>
                  </div>
                  <button 
                    className="dismiss-warning"
                    onClick={() => setPatientMismatch(null)}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Confidence Indicator */}
              {(analysisResult?.confidence || analysisResult?.data?.confidence) && (
                <div className={`confidence-indicator ${analysisResult.confidence || analysisResult.data?.confidence}`}>
                  <span className="confidence-label">Analysis Confidence:</span>
                  <span className="confidence-value">{analysisResult.confidence || analysisResult.data?.confidence}</span>
                </div>
              )}

              {/* AI Notes Section */}
              {(analysisResult?.notes || analysisResult?.data?.notes) && (
                <div className="ai-notes-section">
                  <div className="ai-notes-header">
                    <AlertCircle size={16} />
                    <span>Analysis Notes</span>
                  </div>
                  <p className="ai-notes-content">
                    {analysisResult.notes || analysisResult.data?.notes}
                  </p>
                </div>
              )}

              {/* Detected Language Indicator */}
              <div className="ai-notes-section" style={{ marginTop: '10px' }}>
                <div className="ai-notes-header">
                  <Globe size={16} />
                  <span>Document Language</span>
                </div>
                <p className="ai-notes-content">
                  {analysisResult?.data?.detected_language || 'English'}
                </p>
              </div>

              {/* Vaccinations Form */}
              <div className="vaccinations-form">
                <div className="form-header">
                  <h3>Extracted Vaccinations ({vaccinations.length})</h3>
                  <button 
                    className="btn-add-manual"
                    onClick={addEmptyVaccination}
                  >
                    <Plus size={16} />
                    Add Manually
                  </button>
                </div>

                {vaccinations.length === 0 ? (
                  <div className="no-vaccinations">
                    <p>No vaccinations to review. Add one manually.</p>
                  </div>
                ) : (
                  <div className="vaccination-entries">
                    {vaccinations.map((vax, idx) => (
                      <div 
                        key={vax.id} 
                        className={`vaccination-entry ${duplicateWarnings[vax.id] ? 'has-warning' : ''}`}
                      >
                        <div className="entry-header">
                          <span className="entry-number">#{idx + 1}</span>
                          <span className="entry-source">
                            {vax.source === 'vision_upload' ? '📷 Extracted' : '✏️ Manual'}
                          </span>
                          <button 
                            className="btn-remove-entry"
                            onClick={() => removeVaccination(vax.id)}
                            aria-label="Remove vaccination"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        {duplicateWarnings[vax.id] && (
                          <div className="duplicate-warning">
                            <AlertTriangle size={14} />
                            {duplicateWarnings[vax.id]}
                          </div>
                        )}

                        <div className="entry-fields">
                          <div className="field-row">
                            <div className="field required">
                              <label>
                                <Syringe size={14} />
                                Vaccine Type *
                              </label>
                              <input
                                type="text"
                                value={vax.vaccine_type}
                                onChange={(e) => updateVaccination(vax.id, 'vaccine_type', e.target.value)}
                                placeholder="e.g., COVID-19, Influenza"
                              />
                            </div>
                            <div className="field required">
                              <label>
                                <Calendar size={14} />
                                Date Administered *
                              </label>
                              <input
                                type="date"
                                value={vax.date_administered}
                                onChange={(e) => updateVaccination(vax.id, 'date_administered', e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                              />
                            </div>
                          </div>

                          <div className="field-row">
                            <div className="field">
                              <label>Dose Label</label>
                              <input
                                type="text"
                                value={vax.dose_label}
                                onChange={(e) => updateVaccination(vax.id, 'dose_label', e.target.value)}
                                placeholder="e.g., 1st Dose, Booster"
                              />
                            </div>
                            <div className="field">
                              <label>
                                <Factory size={14} />
                                Manufacturer
                              </label>
                              <input
                                type="text"
                                value={vax.manufacturer}
                                onChange={(e) => updateVaccination(vax.id, 'manufacturer', e.target.value)}
                                placeholder="e.g., Pfizer, Moderna"
                              />
                            </div>
                          </div>

                          <div className="field-row">
                            <div className="field">
                              <label>
                                <Hash size={14} />
                                Lot Number
                              </label>
                              <input
                                type="text"
                                value={vax.lot_number}
                                onChange={(e) => updateVaccination(vax.id, 'lot_number', e.target.value)}
                                placeholder="e.g., EN6201"
                              />
                            </div>
                            <div className="field">
                              <label>
                                <Building2 size={14} />
                                Healthcare Site
                              </label>
                              <input
                                type="text"
                                value={vax.healthcare_site}
                                onChange={(e) => updateVaccination(vax.id, 'healthcare_site', e.target.value)}
                                placeholder="e.g., CVS Pharmacy"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {saveError && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  {saveError}
                </div>
              )}

              <div className="review-actions">
                <button className="btn-secondary" onClick={handleRetry}>
                  <RefreshCw size={16} />
                  Upload Different Image
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleSave}
                  disabled={vaccinations.length === 0}
                >
                  <CheckCircle size={16} />
                  Save {vaccinations.length} Vaccination{vaccinations.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}

          {/* Saving Step */}
          {step === 'saving' && (
            <div className="saving-step">
              {saveSuccess ? (
                <>
                  <div className="success-icon">
                    <CheckCircle size={64} />
                  </div>
                  <h3>Vaccinations Saved!</h3>
                  <p>
                    Successfully added {vaccinations.length} vaccination record{vaccinations.length !== 1 ? 's' : ''} to the patient's record.
                  </p>
                </>
              ) : (
                <>
                  <div className="saving-spinner">
                    <Loader size={48} className="spinner" />
                  </div>
                  <h3>Saving Vaccinations...</h3>
                  <p>Adding records to patient's medical history.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VaccinationUploadDialog